// @flow
import type {ThunkAction} from 'modules/Common/types'
import type {Action} from 'modules/Common/types'
import config from 'config'
import * as adminAPI from 'api/admin'
import {actionTypes} from './constants'
import {actionTypes as authenticateActionTypes} from 'modules/Authenticate/constants'
import {loginFailure, bindDevice} from 'modules/Authenticate/actions'

import {actionCreator} from 'modules/Common/utils/index'

import type {AMNewDetail} from './types/index'
import type {CompanyDetail, ClientNewDetail} from 'modules/Admin/types/index'

export const getAccountManagers = (
  pageNumber: number,
  userIds?: Array<string>,
  companyId?: string
): ThunkAction<Action<any>> => (dispatch, getState) => {
  return actionCreator({
    actionType: actionTypes.GET_ACCOUNT_MANAGERS,
    apiFunction: adminAPI.getListAccountManagers,
    requestPayload: () => {
      const pageSize = getState().admin.accountManagers.pageSize
      return {pageNumber, pageSize, userIds, companyId}
    },
    successPayload: response => ({
      records: response.records,
      pageNumber: response.pageNumber,
      numberOfPages: response.numberOfPages
    })
  })(dispatch, getState)
}

export const getRolesList = (
  roleType: string,
): ThunkAction<Action<any>> => (dispatch, getState) => {
  return actionCreator({
    actionType: actionTypes.GET_ROLES,
    apiFunction: adminAPI.getRolesList,
    requestPayload: () => ({roleType}),
    successPayload: response => ({
      records: response.roles ? (response.roles.roles || []) : []
    })
  })(dispatch, getState)
}

export const getCompanies = (
  pageNumber: number,
  companyIds?: Array<string>
): ThunkAction<Action<any>> => (dispatch, getState) => {
  return actionCreator({
    actionType: actionTypes.GET_COMPANIES,
    apiFunction: adminAPI.getListCompanies,
    requestPayload: () => {
      const pageSize = getState().admin.companies.pageSize
      return {pageNumber, pageSize, companyIds}
    },
    successPayload: response => ({
      records: response.records,
      pageNumber: response.pageNumber,
      numberOfPages: response.numberOfPages
    })
  })(dispatch, getState)
}

export const getCompaniesSuggestion = (
  query: string
): ThunkAction<Action<any>> => (dispatch, getState) => {
  return actionCreator({
    actionType: actionTypes.GET_COMPANIES_SUGGESTION,
    apiFunction: adminAPI.getListCompanies,
    requestPayload: () => {
      const pageSize = 50
      const pageNumber = 1
      const CompanyIds = undefined
      return {pageNumber, pageSize, CompanyIds, query}
    },
    successPayload: response => ({
      records: response.records,
      pageNumber: response.pageNumber,
      numberOfPages: response.numberOfPages
    })
  })(dispatch, getState)
}

export const addOrUpdateAM = (
  detail: AMNewDetail
): ThunkAction<Action<any>> => (dispatch, getState) => {
  return actionCreator({
    actionType: actionTypes.ADD_OR_UPDATE_AM,
    apiFunction: adminAPI.inviteOrUpdateNewAM,
    requestPayload: () => {
      return {detail}
    },
    successPayload: response => ({record: response.record}),
    isGetRawError: true
  })(dispatch, getState)
}

export const login = (
  name: string,
  password: string
): ThunkAction<Action<any>> => (dispatch, getState) => {
  dispatch({
    type: authenticateActionTypes.LOGIN_REQUEST,
    payload: {name, password}
  })

  if (config.FIPS_ENABLED) {
    return adminAPI
      .nativeLogin({
        username: name,
        password
      })
      .then(response => {
        dispatch(persistAdminSession(response))
      })
      .catch(error => {
        dispatch(loginFailure(name, password, '', error.toString()))
        localStorage.removeItem('session')
      })
  }

  let params = {
    username: name,
    realm: 'LeapXpert'
  }

  return adminAPI
    .digestLogin(params, true)
    .then(async response => {
      params = {
        ...params,
        password,
        nonce: response.nonce,
        qop: response.qop,
        realm: response.realm
      }
      try {
        const secondResponse = await adminAPI.digestLogin(params, false)
        dispatch(persistAdminSession(secondResponse))
      } catch (err) {
        dispatch(loginFailure(name, password, '', err))
        localStorage.removeItem('session')
      }
    })
    .catch(error => {
      dispatch(loginFailure(name, password, '', error.toString()))
      localStorage.removeItem('session')
    })
}

const verifyLoginSuccess = (
  accessToken: string,
  userId: string,
  companyId: string,
  isAdmin: boolean
) => {
  return {
    type: authenticateActionTypes.VERIFY_LOGIN_SUCCESS,
    payload: {
      accessToken: accessToken,
      matrixAccessToken: '',
      matrixId: '',
      userId: userId,
      companyId: companyId,
      isAdmin: isAdmin
    }
  }
}

const persistAdminSession = (response: {
  accessToken: string,
  id: string,
  companyId: string
}): ThunkAction<Action<any>> => (dispatch, getState) => {
  dispatch(
    verifyLoginSuccess(
      response.accessToken,
      response.id,
      response.companyId,
      true
    )
  )
  let session = JSON.parse(localStorage.getItem('session') || '{}')
  session = {
    ...session,
    accessToken: response.accessToken,
    userId: response.id,
    companyId: response.companyId,
    isAdmin: false
  }

  localStorage.setItem('session', JSON.stringify(session))
  dispatch(bindDevice())
}

export const updateCompanyDefaultValue = (
  companyId: string
): ThunkAction<Action<any>> => (dispatch, getState) => {
  return actionCreator({
    actionType: '',
    apiFunction: adminAPI.updateCompanyDefaultValue,
    requestPayload: () => {
      return {companyId}
    },
    successPayload: response => {
      return response
    }
  })(dispatch, getState)
}

export const addOrUpdateCompany = (
  detail: {
    company: CompanyDetail
  },
  isAddNewCompany?: boolean
): ThunkAction<any> => (dispatch, getState) => {
  return actionCreator({
    actionType: actionTypes.ADD_OR_UPDATE_COMPANY,
    apiFunction: adminAPI.addOrUpdateNewCompany,
    requestPayload: () => {
      return {detail}
    },
    successPayload: response => ({record: response.record}),
    isGetRawError: true,
    taskAfterSuccess: response => {
      if (isAddNewCompany)
        dispatch(updateCompanyDefaultValue(response.record.id))
      return response
    }
  })(dispatch, getState)
}

export const getClients = (pageNumber: number): ThunkAction<Action<any>> => (
  dispatch,
  getState
) => {
  return actionCreator({
    actionType: actionTypes.GET_CLIENTS,
    apiFunction: adminAPI.getClients,
    requestPayload: () => {
      const pageSize = getState().admin.clients.pageSize
      return {pageNumber, pageSize}
    },
    successPayload: response => ({
      records: response.records,
      pageNumber: response.pageNumber,
      numberOfPages: response.numberOfPages
    })
  })(dispatch, getState)
}

export const inviteNewClient = (
  detail: ClientNewDetail,
  isUpdateClient?: boolean
): ThunkAction<any> => (dispatch, getState) => {
  return actionCreator({
    actionType: actionTypes.INVITE_NEW_CLIENT,
    apiFunction: adminAPI.inviteNewClient,
    requestPayload: () => ({
      detail
    }),
    successPayload: response => ({record: response.record})
  })(dispatch, getState)
}

export const getAccountManagersSuggestion = (
  query: string,
  companyId: string
): ThunkAction<Action<any>> => (dispatch, getState) => {
  return actionCreator({
    actionType: actionTypes.GET_ACCOUNT_MANAGERS_SUGGESTION,
    apiFunction: adminAPI.getListAccountManagers,
    requestPayload: () => {
      const pageSize = 50
      const pageNumber = 1
      const userIds = undefined
      return {pageNumber, pageSize, userIds, companyId, query}
    },
    successPayload: response => ({
      records: response.records,
      pageNumber: response.pageNumber,
      numberOfPages: response.numberOfPages
    })
  })(dispatch, getState)
}

export const getCompanyProfile = (
  companyId: string
): ThunkAction<Action<any>> => (dispatch, getState) => {
  return actionCreator({
    actionType: actionTypes.GET_COMPANY_PROFILE,
    apiFunction: adminAPI.getCompanyProfile,
    requestPayload: () => ({companyId}),
    successPayload: response => ({
      profile: response.profile
    })
  })(dispatch, getState)
}
